import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { deleteConfirmAlert } from "../../../components/Alert";
import GlobalButton from "../../../components/CustomButton";

const BlogsFaqs = ({ faqs, setFaqs, faqsDialogRef }) => {
    const { t } = useTranslation(["blog", "faq", "common"]);
    const { currentLanguage } = useSelector((state) => state.languageReducer);

    const getFaqQuestion = (faq) =>
        faq?.question?.[currentLanguage] ||
        faq?.question?.en ||
        faq?.question?.ro ||
        "";

    const getFaqAnswer = (faq) =>
        faq?.answer?.[currentLanguage] ||
        faq?.answer?.en ||
        faq?.answer?.ro ||
        "";

    const handleEditFaq = (faq) => {
        faqsDialogRef?.current?.openDialog({
            type: "edit",
            data: faq,
        });
    };

    const handleDeleteFaq = (faq) => {
        deleteConfirmAlert({
            title: t("deleteConfirmation", { ns: "common" }),
            text: t("deleteFaqConfirmation", { ns: "faq" }),
            confirmButtonText: t("delete", { ns: "common" }),
            cancelButtonText: t("cancel", { ns: "common" }),
        }).then((result) => {
            if (result) {
                setFaqs((prev) =>
                    prev.filter((item) =>
                        faq._id ? item._id !== faq._id : item !== faq
                    )
                );
            }
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                mt: 3,
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        {t("faqs")}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {t("optional", { ns: "common" })}
                    </Typography>
                </Box>

                <GlobalButton
                    btnText="addFaq"
                    handleClickBtn={() =>
                        faqsDialogRef?.current?.openDialog({
                            type: "add",
                        })
                    }
                    styleProps={{
                        width: "auto",
                        minWidth: 180,
                    }}
                />
            </Stack>

            {faqs?.length > 0 ? (
                <Box mt={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {t("faqsCount", { count: faqs.length })}
                        </Typography>

                        <Chip
                            color="primary"
                            size="small"
                            label={`${faqs.length} ${t("faqs")}`}
                        />
                    </Stack>

                    <Stack spacing={1.5}>
                        {faqs.map((faq, index) => (
                            <Accordion
                                key={faq._id || `faq-${index}`}
                                disableGutters
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "12px !important",
                                    overflow: "hidden",
                                    "&:before": { display: "none" },
                                    bgcolor: "background.default",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        minHeight: 56,
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            gap: 1.5,
                                            my: 1,
                                        },
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        icon={<QuizOutlinedIcon sx={{ fontSize: 16 }} />}
                                        label={`#${index + 1}`}
                                        sx={{ fontWeight: 600 }}
                                    />

                                    <Typography
                                        fontWeight={600}
                                        sx={{
                                            flex: 1,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {getFaqQuestion(faq) || t("noFaqsAdded")}
                                    </Typography>

                                    <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title={t("edit", { ns: "common" })}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditFaq(faq)}
                                                sx={{
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={t("delete", { ns: "common" })}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteFaq(faq)}
                                                sx={{
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: "background.paper",
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            display="block"
                                            mb={0.75}
                                        >
                                            {t("answer", { ns: "faq" })}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
                                        >
                                            {getFaqAnswer(faq) || "-"}
                                        </Typography>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Box
                    mt={3}
                    sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        py: 4,
                        px: 2,
                        textAlign: "center",
                        bgcolor: "background.default",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t("noFaqsAdded")}
                    </Typography>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={1}
                    >
                        {t("clickAddFaqToAddFaq")}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default BlogsFaqs;
